/**
 * Hook to initialize PlayerCoreStore when player session is available.
 * Should be used once at app root level (e.g., in App.tsx or a layout component).
 * 
 * CRITICAL: This hook now checks for route type:
 * - On /module/* routes: Skip full PlayerCoreStore initialization (no time_periods, no financial status)
 * - On /freeworld, /student, /city, /dashboard: Full initialization with all data
 * 
 * This prevents FreeWorld data from loading on module routes.
 */

import { useEffect, useRef } from 'react';
import { usePlayerAuth } from './usePlayerAuth';
import { usePlayerCoreStore } from '@/stores/usePlayerCoreStore';
import { isModuleRoute, isFreeWorldRoute } from './useModuleBootstrap';

export const usePlayerCoreInitializer = () => {
  const { playerSession, loading: authLoading } = usePlayerAuth();
  const initialize = usePlayerCoreStore((state) => state.initialize);
  const reset = usePlayerCoreStore((state) => state.reset);
  const isReady = usePlayerCoreStore((state) => state.isReady);
  const storePlayerId = usePlayerCoreStore((state) => state.playerId);
  
  const initializingRef = useRef(false);
  const lastPlayerIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Don't do anything while auth is still loading
    if (authLoading) {
      return;
    }

    // CRITICAL: Skip full initialization on module routes
    // Module routes don't need time_periods, financial status, or other FreeWorld data
    if (isModuleRoute()) {
      if (import.meta.env.DEV) {
        console.debug('[PlayerCoreInit] SKIPPED on module route - no FreeWorld data needed');
      }
      return;
    }

    // If we have a player session, initialize the core store
    if (playerSession?.playerId && playerSession?.sessionToken) {
      // Only initialize if player changed or not yet ready
      if (lastPlayerIdRef.current !== playerSession.playerId || !isReady) {
        if (!initializingRef.current) {
          initializingRef.current = true;
          lastPlayerIdRef.current = playerSession.playerId;
          
          if (import.meta.env.DEV) {
            console.log('[PlayerCoreInit] Initializing for', playerSession.playerId.slice(0, 8) + '...');
          }
          
          initialize(
            playerSession.playerId,
            playerSession.playerName,
            playerSession.groupId,
            playerSession.sessionToken
          ).finally(() => {
            initializingRef.current = false;
          });
        }
      }
    } else if (!playerSession && storePlayerId) {
      // Player logged out, reset the store
      reset();
      lastPlayerIdRef.current = null;
    }
  }, [playerSession, authLoading, initialize, reset, isReady, storePlayerId]);

  // Listen for events that should trigger a refresh
  // ONLY on non-module routes
  useEffect(() => {
    // Skip event listeners on module routes
    if (isModuleRoute()) {
      return;
    }

    const refresh = usePlayerCoreStore.getState().refresh;
    
    const handleTimePeriodChanged = () => {
      if (usePlayerCoreStore.getState().isReady) {
        refresh();
      }
    };

    const handleFreeWorldReset = () => {
      if (usePlayerCoreStore.getState().isReady) {
        refresh();
      }
    };

    window.addEventListener('timePeriodChanged', handleTimePeriodChanged);
    window.addEventListener('freeWorldReset', handleFreeWorldReset);

    return () => {
      window.removeEventListener('timePeriodChanged', handleTimePeriodChanged);
      window.removeEventListener('freeWorldReset', handleFreeWorldReset);
    };
  }, []);
};
